<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { vOnClickOutside } from '@vueuse/components';
import HeaderSection from '../components/HeaderSection.vue';
import BodySection from '../components/BodySection.vue';
import FooterSection from '../components/FooterSection.vue';
import ButtonsSection from '../components/ButtonsSection.vue';
import TemplatePreview from '../components/TemplatePreview.vue';
import {
  generateTemplateComponents,
  validateTemplateData,
} from 'dashboard/helper/templateHelper';

const { t, te } = useI18n();

const getRejectedReason = reason => {
  if (!reason) return '';
  const key = `MESSAGE_TEMPLATES.REJECTED_REASONS.${reason.toUpperCase()}`;
  return te(key) ? t(key) : reason;
};
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

// ── Edit mode detection ─────────────────────────────────────────────
const isEditMode = computed(() => !!route.params.templateId);
const templateId = computed(() => route.params.templateId);
const isLoading = ref(false);

// Form metadata
const templateName = ref('');
const selectedCategory = ref('marketing');
const selectedLanguage = ref('pt_BR');
const selectedInboxId = computed(() => route.params.inboxId);
const templateStatus = ref('');
const rejectedReason = ref('');
const parameterType = ref('positional');
const showParamDropdown = ref(false);
const isSubmitting = ref(false);
const isDirty = ref(false);
const isInitializing = ref(true);
const hasAttemptedSubmit = ref(false);

// Unsaved changes guard
onBeforeRouteLeave(() => {
  if (isDirty.value && !isSubmitting.value) {
    // eslint-disable-next-line no-alert
    const leave = window.confirm(
      t('MESSAGE_TEMPLATES.BUILDER.UNSAVED_CHANGES')
    );
    if (!leave) return false;
  }
  return true;
});

function handleBeforeUnload(e) {
  if (isDirty.value) {
    e.preventDefault();
    e.returnValue = '';
  }
}

onMounted(() => window.addEventListener('beforeunload', handleBeforeUnload));
onBeforeUnmount(() =>
  window.removeEventListener('beforeunload', handleBeforeUnload)
);

// Template data (modular sections)
const templateData = ref({
  header: {
    enabled: false,
    type: 'HEADER',
    format: 'TEXT',
    text: '',
    example: {
      header_text: [],
      header_text_named_params: [],
    },
    media: {},
    error: '',
  },
  body: {
    type: 'BODY',
    text: '',
    example: {
      body_text: [],
      body_text_named_params: [],
    },
    error: '',
  },
  footer: {
    type: 'FOOTER',
    enabled: false,
    text: '',
  },
  buttons: [],
});

// Track dirty state on form changes
watch(
  [
    templateName,
    selectedCategory,
    selectedLanguage,
    selectedInboxId,
    templateData,
  ],
  () => {
    if (!isInitializing.value) {
      isDirty.value = true;
    }
  },
  { deep: true }
);

const categories = [
  { value: 'marketing', label: 'MESSAGE_TEMPLATES.CATEGORIES.MARKETING' },
  { value: 'utility', label: 'MESSAGE_TEMPLATES.CATEGORIES.UTILITY' },
  {
    value: 'authentication',
    label: 'MESSAGE_TEMPLATES.CATEGORIES.AUTHENTICATION',
  },
];
const categoryOptions = computed(() =>
  categories.map(c => ({ value: c.value, label: t(c.label) }))
);

const languages = [
  { value: 'af', label: 'Afrikaans' },
  { value: 'sq', label: 'Albanian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'az', label: 'Azerbaijani' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'ca', label: 'Catalan' },
  { value: 'zh_CN', label: 'Chinese (Simplified)' },
  { value: 'zh_HK', label: 'Chinese (Hong Kong)' },
  { value: 'zh_TW', label: 'Chinese (Traditional)' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'en', label: 'English' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'et', label: 'Estonian' },
  { value: 'fil', label: 'Filipino' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'ka', label: 'Georgian' },
  { value: 'de', label: 'German' },
  { value: 'el', label: 'Greek' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ha', label: 'Hausa' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ga', label: 'Irish' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'kk', label: 'Kazakh' },
  { value: 'rw_RW', label: 'Kinyarwanda' },
  { value: 'ko', label: 'Korean' },
  { value: 'ky_KG', label: 'Kyrgyz' },
  { value: 'lo', label: 'Lao' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mr', label: 'Marathi' },
  { value: 'nb', label: 'Norwegian (Bokmål)' },
  { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt_BR', label: 'Portuguese (BR)' },
  { value: 'pt_PT', label: 'Portuguese (PT)' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ro', label: 'Romanian' },
  { value: 'ru', label: 'Russian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'es', label: 'Spanish' },
  { value: 'es_AR', label: 'Spanish (Argentina)' },
  { value: 'es_MX', label: 'Spanish (Mexico)' },
  { value: 'sw', label: 'Swahili' },
  { value: 'sv', label: 'Swedish' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'uz', label: 'Uzbek' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'zu', label: 'Zulu' },
];

const parameterTypeOptions = [
  {
    label: t('MESSAGE_TEMPLATES.BUILDER.PARAMETER_TYPE.POSITIONAL'),
    description: t('MESSAGE_TEMPLATES.BUILDER.PARAMETER_TYPE.POSITIONAL_DESC'),
    value: 'positional',
    action: 'set-positional',
  },
  {
    label: t('MESSAGE_TEMPLATES.BUILDER.PARAMETER_TYPE.NAMED'),
    description: t('MESSAGE_TEMPLATES.BUILDER.PARAMETER_TYPE.NAMED_DESC'),
    value: 'named',
    action: 'set-named',
  },
];

// Validation
const isNameValid = computed(() => /^[a-z0-9_]+$/.test(templateName.value));
const isFormValid = computed(() => {
  return (
    templateName.value.length > 0 &&
    isNameValid.value &&
    selectedInboxId.value &&
    validateTemplateData(templateData.value)
  );
});

// Per-field validation hints (shown after first submit attempt)
const showNameError = computed(
  () => hasAttemptedSubmit.value && templateName.value.length === 0
);

// Submit
const handleSubmit = async () => {
  hasAttemptedSubmit.value = true;
  if (!isFormValid.value || isSubmitting.value) return;
  isSubmitting.value = true;

  const components = generateTemplateComponents(
    templateData.value,
    parameterType.value
  );

  const payload = {
    message_template: {
      name: templateName.value,
      category: selectedCategory.value,
      language: selectedLanguage.value,
      channel_type: 'Channel::Whatsapp',
      inbox_id: selectedInboxId.value,
      content: { components, parameter_format: parameterType.value },
    },
  };

  try {
    if (isEditMode.value) {
      await store.dispatch('messageTemplates/update', {
        id: templateId.value,
        ...payload,
      });
      useAlert(t('MESSAGE_TEMPLATES.BUILDER.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('messageTemplates/create', payload);
      useAlert(t('MESSAGE_TEMPLATES.BUILDER.SUCCESS'));
    }
    isDirty.value = false;
    router.push({
      name: 'message_templates_list',
      params: { inboxId: selectedInboxId.value },
    });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.message ||
      t('MESSAGE_TEMPLATES.BUILDER.ERROR');
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const goBack = () => {
  router.push({
    name: 'message_templates_list',
    params: { inboxId: selectedInboxId.value },
  });
};

const handleParameterTypeAction = action => {
  if (action.action === 'set-positional') {
    parameterType.value = 'positional';
  } else if (action.action === 'set-named') {
    parameterType.value = 'named';
  }
  showParamDropdown.value = false;
};

// Auto-format name
watch(templateName, val => {
  templateName.value = val.toLowerCase().replace(/[^a-z0-9_]/g, '_');
});

// ── Load existing template for edit mode ────────────────────────────
function populateFromComponent(comp, section) {
  if (!comp) return;
  if (comp.type === 'HEADER') {
    section.header.enabled = true;
    section.header.format = comp.format || 'TEXT';
    section.header.text = comp.text || '';
    if (comp.example?.header_text) {
      section.header.example.header_text = comp.example.header_text;
    }
    if (comp.example?.header_text_named_params) {
      section.header.example.header_text_named_params =
        comp.example.header_text_named_params;
    }
  } else if (comp.type === 'BODY') {
    section.body.text = comp.text || '';
    if (comp.example?.body_text) {
      const bt = comp.example.body_text;
      section.body.example.body_text = Array.isArray(bt[0]) ? bt[0] : bt;
    }
    if (comp.example?.body_text_named_params) {
      section.body.example.body_text_named_params =
        comp.example.body_text_named_params;
    }
  } else if (comp.type === 'FOOTER') {
    section.footer.enabled = true;
    section.footer.text = comp.text || '';
  } else if (comp.type === 'BUTTONS') {
    section.buttons = (comp.buttons || []).map(btn => ({ ...btn }));
  }
}

async function loadExistingTemplate() {
  if (!isEditMode.value) return;

  isLoading.value = true;
  try {
    // Ensure templates are loaded
    if (!getters['messageTemplates/getMessageTemplates'].value.length) {
      await store.dispatch('messageTemplates/get');
    }

    const tmpl = getters['messageTemplates/getTemplateById'].value(
      templateId.value
    );
    if (!tmpl) {
      useAlert(t('MESSAGE_TEMPLATES.BUILDER.NOT_FOUND'));
      router.push({
        name: 'message_templates_list',
        params: { inboxId: selectedInboxId.value },
      });
      return;
    }

    templateName.value = tmpl.name || '';
    selectedCategory.value = tmpl.category || 'marketing';
    selectedLanguage.value = tmpl.language || 'pt_BR';
    templateStatus.value = tmpl.status || '';
    rejectedReason.value = tmpl.metadata?.rejected_reason || '';

    // Populate sections from content.components
    const components = tmpl.content?.components || [];
    const data = JSON.parse(JSON.stringify(templateData.value));
    components.forEach(comp => populateFromComponent(comp, data));
    templateData.value = data;
    if (tmpl.content?.parameter_format) {
      parameterType.value = tmpl.content.parameter_format;
    }
  } finally {
    isLoading.value = false;
    await nextTick();
    isDirty.value = false;
    isInitializing.value = false;
  }
}

async function loadCloneSource() {
  const cloneFromId = route.query.cloneFrom;
  if (!cloneFromId) return;

  isLoading.value = true;
  try {
    if (!getters['messageTemplates/getMessageTemplates'].value.length) {
      await store.dispatch('messageTemplates/get');
    }

    const tmpl = getters['messageTemplates/getTemplateById'].value(cloneFromId);
    if (!tmpl) return;

    templateName.value = `${tmpl.name}_copy`;
    selectedCategory.value = tmpl.category || 'marketing';
    selectedLanguage.value = tmpl.language || 'pt_BR';

    const components = tmpl.content?.components || [];
    const data = JSON.parse(JSON.stringify(templateData.value));
    components.forEach(comp => populateFromComponent(comp, data));
    templateData.value = data;
    if (tmpl.content?.parameter_format) {
      parameterType.value = tmpl.content.parameter_format;
    }
  } finally {
    isLoading.value = false;
    await nextTick();
    isDirty.value = false;
    isInitializing.value = false;
  }
}

function applyDefaultFooter() {
  if (isEditMode.value || route.query.cloneFrom) return;
  const expectedText = 'Para não receber mais avisos, responda #sair';
  if (
    (selectedCategory.value === 'utility' ||
      selectedCategory.value === 'marketing') &&
    selectedLanguage.value === 'pt_BR'
  ) {
    if (
      !templateData.value.footer.text ||
      templateData.value.footer.text === expectedText
    ) {
      templateData.value.footer.enabled = true;
      templateData.value.footer.text = expectedText;
    }
  } else if (templateData.value.footer.text === expectedText) {
    templateData.value.footer.enabled = false;
    templateData.value.footer.text = '';
  }
}

watch([selectedCategory, selectedLanguage], () => {
  if (!isInitializing.value) {
    applyDefaultFooter();
  }
});

onMounted(async () => {
  if (isEditMode.value) {
    await loadExistingTemplate();
  } else if (route.query.cloneFrom) {
    await loadCloneSource();
  } else {
    applyDefaultFooter();
  }
  // For new templates (no edit, no clone), allow dirty tracking after mount settles
  if (isInitializing.value) {
    await nextTick();
    isInitializing.value = false;
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header bar -->
    <header class="sticky top-0 z-10 px-6 border-b border-n-strong">
      <div class="w-full max-w-7xl mx-auto">
        <div class="flex items-center justify-between w-full h-16 gap-2">
          <div class="flex items-center gap-3">
            <Button
              icon="i-lucide-arrow-left"
              size="sm"
              variant="ghost"
              color-scheme="secondary"
              @click="goBack"
            />
            <div class="flex items-center gap-2 text-sm text-n-slate-11">
              <span
                class="cursor-pointer hover:text-n-slate-12"
                @click="goBack"
              >
                {{ t('MESSAGE_TEMPLATES.HEADER_TITLE') }}
              </span>
              <span class="i-lucide-chevron-right size-4" />
              <span class="text-n-slate-12 font-medium">
                {{
                  isEditMode
                    ? t('MESSAGE_TEMPLATES.BUILDER.EDIT_TITLE')
                    : t('MESSAGE_TEMPLATES.BUILDER.TITLE')
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              :label="t('MESSAGE_TEMPLATES.BUILDER.DISCARD')"
              variant="ghost"
              color="slate"
              size="sm"
              @click="goBack"
            />
            <Button
              :label="
                isEditMode
                  ? t('MESSAGE_TEMPLATES.BUILDER.UPDATE')
                  : t('MESSAGE_TEMPLATES.BUILDER.SUBMIT')
              "
              :icon="isEditMode ? 'i-lucide-save' : 'i-lucide-send'"
              size="sm"
              :disabled="!isFormValid"
              :is-loading="isSubmitting"
              @click="handleSubmit"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6 py-6">
      <!-- Loading state for edit/clone mode -->
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-20 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div v-else class="w-full max-w-7xl mx-auto space-y-6">
        <!-- Rejection alert -->
        <div
          v-if="isEditMode && templateStatus === 'rejected' && rejectedReason"
          class="p-4 rounded-lg bg-n-ruby-3 border border-n-ruby-6 flex gap-3 text-n-ruby-11"
        >
          <span class="i-lucide-alert-circle size-5 shrink-0 mt-0.5" />
          <div>
            <h4 class="text-sm font-medium">
              {{ t('MESSAGE_TEMPLATES.BUILDER.REJECTED_ALERT') }}
            </h4>
            <p class="text-sm mt-1">{{ getRejectedReason(rejectedReason) }}</p>
          </div>
        </div>

        <div
          class="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start"
        >
          <!-- Left: Builder form -->
          <div class="flex flex-col gap-6">
            <!-- Template metadata card -->
            <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
              <div class="flex items-center gap-3 mb-4">
                <span class="i-lucide-file-text size-5 text-n-slate-11" />
                <h4 class="font-medium text-n-slate-12">
                  {{ t('MESSAGE_TEMPLATES.BUILDER.TEMPLATE_INFO') }}
                </h4>
              </div>

              <div class="space-y-4">
                <!-- Template name -->
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-2">
                    {{ t('MESSAGE_TEMPLATES.BUILDER.NAME_LABEL') }}
                  </label>
                  <input
                    id="templateName"
                    v-model="templateName"
                    name="template_name"
                    type="text"
                    :placeholder="
                      t('MESSAGE_TEMPLATES.BUILDER.NAME_PLACEHOLDER')
                    "
                    :readonly="isEditMode"
                    class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
                    :class="{
                      'border-n-ruby-9':
                        templateName.length > 0 && !isNameValid,
                      'opacity-60 cursor-not-allowed': isEditMode,
                    }"
                  />
                  <p
                    v-if="templateName.length > 0 && !isNameValid"
                    class="text-xs text-n-ruby-11 mt-1"
                  >
                    {{ t('MESSAGE_TEMPLATES.BUILDER.NAME_HINT') }}
                  </p>
                  <p
                    v-else-if="showNameError"
                    class="text-xs text-n-ruby-11 mt-1"
                  >
                    {{ t('MESSAGE_TEMPLATES.BUILDER.NAME_REQUIRED') }}
                  </p>
                </div>

                <!-- Category + Language row -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      class="block text-sm font-medium text-n-slate-12 mb-2"
                    >
                      {{ t('MESSAGE_TEMPLATES.BUILDER.CATEGORY_LABEL') }}
                    </label>
                    <Select
                      id="category"
                      v-model="selectedCategory"
                      name="category"
                      :options="categoryOptions"
                      class="w-full"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-sm font-medium text-n-slate-12 mb-2"
                    >
                      {{ t('MESSAGE_TEMPLATES.BUILDER.LANGUAGE_LABEL') }}
                    </label>
                    <Select
                      id="language"
                      v-model="selectedLanguage"
                      name="language"
                      :options="languages"
                      class="w-full"
                    />
                  </div>
                </div>

                <!-- Parameter type selector -->
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-2">
                    {{ t('MESSAGE_TEMPLATES.BUILDER.PARAMETER_TYPE.LABEL') }}
                  </label>
                  <div
                    v-on-click-outside="() => (showParamDropdown = false)"
                    class="relative inline-block"
                  >
                    <Button
                      :label="
                        parameterType === 'positional'
                          ? t(
                              'MESSAGE_TEMPLATES.BUILDER.PARAMETER_TYPE.POSITIONAL'
                            )
                          : t('MESSAGE_TEMPLATES.BUILDER.PARAMETER_TYPE.NAMED')
                      "
                      trailing-icon
                      :icon="
                        showParamDropdown
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      variant="outline"
                      color="slate"
                      size="sm"
                      @click="showParamDropdown = !showParamDropdown"
                    />
                    <DropdownMenu
                      v-if="showParamDropdown"
                      :menu-items="parameterTypeOptions"
                      class="ltr:left-0 rtl:right-0 z-[100] top-full mt-1"
                      @action="handleParameterTypeAction"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Header section -->
            <HeaderSection
              v-model="templateData.header"
              :parameter-type="parameterType"
            />

            <!-- Body section -->
            <BodySection
              v-model="templateData.body"
              :parameter-type="parameterType"
            />

            <!-- Footer section -->
            <FooterSection v-model="templateData.footer" />

            <!-- Buttons section -->
            <ButtonsSection v-model="templateData.buttons" />
          </div>

          <!-- Right: Sticky Preview -->
          <div class="xl:sticky xl:top-[80px]">
            <TemplatePreview
              :template-data="templateData"
              :parameter-type="parameterType"
            />
          </div>
        </div>
      </div>
    </main>
  </section>
</template>
